class NookwiseShippingRates extends HTMLElement {
  connectedCallback() {
    this.country = this.querySelector('[data-shipping-country]');
    this.provinceWrap = this.querySelector('[data-shipping-province-wrap]');
    this.province = this.querySelector('[data-shipping-province]');
    this.postcode = this.querySelector('[data-shipping-postcode]');
    this.button = this.querySelector('[data-shipping-calculate]');
    this.results = this.querySelector('[data-shipping-results]');
    this.status = this.querySelector('[data-shipping-status]');

    if (!this.country || !this.button || !this.results) return;

    this.root = window.Shopify?.routes?.root || '/';
    this.productFormId = this.dataset.productFormId || '';
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'GBP';
    this.defaultCountry = this.dataset.defaultCountry || '';

    if (this.defaultCountry) {
      const matchingOption = [...this.country.options].find(
        (option) => option.value.toLowerCase() === this.defaultCountry.toLowerCase()
      );
      if (matchingOption) this.country.value = matchingOption.value;
    }

    this.updateProvinceOptions();
    this.country.addEventListener('change', () => this.updateProvinceOptions());
    this.button.addEventListener('click', () => this.calculate());
  }

  updateProvinceOptions() {
    if (!this.province || !this.provinceWrap) return;

    const option = this.country.options[this.country.selectedIndex];
    let provinces = [];

    try {
      provinces = JSON.parse(option?.dataset?.provinces || '[]');
    } catch (_error) {
      provinces = [];
    }

    this.province.innerHTML = '';

    if (!Array.isArray(provinces) || provinces.length === 0) {
      this.provinceWrap.hidden = true;
      this.province.disabled = true;
      return;
    }

    provinces.forEach((province) => {
      const opt = document.createElement('option');
      opt.value = province[0] || province[1] || '';
      opt.textContent = province[1] || province[0] || '';
      this.province.appendChild(opt);
    });

    this.province.disabled = false;
    this.provinceWrap.hidden = false;
  }

  getProductForm() {
    if (!this.productFormId) return null;
    return document.getElementById(this.productFormId);
  }

  getSelectedVariantAndQuantity() {
    const form = this.getProductForm();
    if (!form) throw new Error('Product form not found.');

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]')
      || document.querySelector(`input[name="quantity"][form="${CSS.escape(this.productFormId)}"]`)
      || form.closest('product-form-component')?.querySelector('input[name="quantity"]');

    const variantId = Number(variantInput?.value || 0);
    const quantity = Math.max(1, Number(quantityInput?.value || 1));

    if (!variantId) throw new Error('Please choose a product option first.');
    return { variantId, quantity };
  }

  async fetchCart() {
    const response = await fetch(`${this.root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  }

  async addTemporaryProduct(variantId, quantity) {
    const response = await fetch(`${this.root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity }],
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Unable to calculate delivery for this product.');
    }
  }

  async restoreCart(originalCart) {
    const currentCart = await this.fetchCart();
    const originalByKey = new Map(originalCart.items.map((item) => [item.key, item.quantity]));
    const updates = {};

    currentCart.items.forEach((item) => {
      updates[item.key] = originalByKey.has(item.key) ? originalByKey.get(item.key) : 0;
    });

    if (Object.keys(updates).length === 0) return;

    await fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
  }

  addressParams() {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', this.country.value);

    const postcode = this.postcode?.value?.trim();
    if (postcode) params.set('shipping_address[zip]', postcode);

    if (this.province && !this.province.disabled && this.province.value) {
      params.set('shipping_address[province]', this.province.value);
    }

    return params;
  }

  async requestShippingRates() {
    const params = this.addressParams();
    const prepareUrl = `${this.root}cart/prepare_shipping_rates.json?${params.toString()}`;
    const asyncUrl = `${this.root}cart/async_shipping_rates.json?${params.toString()}`;

    const prepareResponse = await fetch(prepareUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!prepareResponse.ok && prepareResponse.status !== 202) {
      const error = await prepareResponse.json().catch(() => ({}));
      throw new Error(error?.description || error?.message || 'No delivery options are available for this address.');
    }

    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 450));

      const response = await fetch(asyncUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);
        if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      }
    }

    // Fallback endpoint. Shopify recommends the async flow above, but this is useful
    // if an installation returns the result synchronously.
    const fallbackResponse = await fetch(`${this.root}cart/shipping_rates.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (fallbackResponse.ok) {
      const data = await fallbackResponse.json();
      if (Array.isArray(data.shipping_rates)) return data.shipping_rates;
    }

    throw new Error('Delivery rates are taking too long to load. Please try again.');
  }

  formatPrice(price, currency) {
    const amount = Number.parseFloat(price);
    if (!Number.isFinite(amount)) return price || '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || this.currency,
      }).format(amount);
    } catch (_error) {
      return `${price} ${currency || this.currency}`;
    }
  }

  renderRates(rates) {
    this.results.innerHTML = '';

    if (!rates.length) {
      this.results.innerHTML = '<p class="nookwise-shipping-rates__empty">No delivery options are available for this destination.</p>';
      return;
    }

    const saved = this.getSavedPreference();

    rates.forEach((rate, index) => {
      const label = document.createElement('label');
      label.className = 'nookwise-shipping-rate';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `nookwise-shipping-rate-${this.dataset.blockId || 'product'}`;
      input.value = rate.name || rate.title || `rate-${index}`;

      const rateName = rate.name || rate.title || 'Delivery';
      const price = this.formatPrice(rate.price, rate.currency);
      const details = rate.delivery_range || rate.delivery_date || rate.description || '';

      if (saved && saved.name === rateName && saved.country === this.country.value && saved.postcode === (this.postcode?.value?.trim() || '')) {
        input.checked = true;
      }

      input.addEventListener('change', () => {
        this.savePreference({
          name: rateName,
          price,
          country: this.country.value,
          postcode: this.postcode?.value?.trim() || '',
        });
      });

      const radio = document.createElement('span');
      radio.className = 'nookwise-shipping-rate__radio';
      radio.setAttribute('aria-hidden', 'true');

      const copy = document.createElement('span');
      copy.className = 'nookwise-shipping-rate__copy';
      const strong = document.createElement('strong');
      strong.textContent = rateName;
      copy.appendChild(strong);
      if (details) {
        const small = document.createElement('small');
        small.textContent = details;
        copy.appendChild(small);
      }

      const priceEl = document.createElement('span');
      priceEl.className = 'nookwise-shipping-rate__price';
      priceEl.textContent = price;

      label.append(input, radio, copy, priceEl);
      this.results.appendChild(label);
    });
  }

  savePreference(preference) {
    try {
      sessionStorage.setItem('nookwise-shipping-preference', JSON.stringify(preference));
    } catch (_error) {
      // Storage can be disabled; the calculator still works without it.
    }
  }

  getSavedPreference() {
    try {
      return JSON.parse(sessionStorage.getItem('nookwise-shipping-preference') || 'null');
    } catch (_error) {
      return null;
    }
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('is-error', isError);
  }

  async calculate() {
    if (this.button.disabled) return;

    if (!this.country.value || this.country.value === '---') {
      this.setStatus('Choose a destination country.', true);
      this.country.focus();
      return;
    }

    if (!this.postcode?.value?.trim()) {
      this.setStatus('Enter your postcode to see the available delivery options.', true);
      this.postcode?.focus();
      return;
    }

    let originalCart = null;
    let temporaryProductAdded = false;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    const originalButtonText = this.button.textContent;
    this.button.textContent = this.button.dataset.loadingText || 'Calculating…';
    this.setStatus('Checking the delivery rates configured in Shopify…');
    this.results.innerHTML = '';

    try {
      const { variantId, quantity } = this.getSelectedVariantAndQuantity();
      originalCart = await this.fetchCart();
      await this.addTemporaryProduct(variantId, quantity);
      temporaryProductAdded = true;

      const rates = await this.requestShippingRates();
      this.renderRates(rates);
      this.setStatus(rates.length ? 'Select the delivery option you prefer.' : '');
    } catch (error) {
      console.error('[Nookwise shipping rates]', error);
      this.setStatus(error.message || 'Unable to calculate delivery. Please try again.', true);
    } finally {
      if (temporaryProductAdded && originalCart) {
        try {
          await this.restoreCart(originalCart);
        } catch (restoreError) {
          console.error('[Nookwise shipping rates] Unable to restore cart', restoreError);
        }
      }

      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
      this.button.textContent = originalButtonText;
    }
  }
}

if (!customElements.get('nookwise-shipping-rates')) {
  customElements.define('nookwise-shipping-rates', NookwiseShippingRates);
}
